/** Groups at or below this row count render every row (no inner virtualizer). */
export const TABLE_ROW_VIRTUALIZATION_THRESHOLD = 60;

/** Initial row height guess; `measureElement` corrects after paint. */
export const TABLE_ROW_ESTIMATED_SIZE_PX = 36;

/**
 * Integer CSS pixel (avoids subpixel spacer/row oscillation).
 * @param {number} value
 * @returns {number}
 */
export function roundCssPx(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

/**
 * Inner-list scrollMargin: group offset + group header height, pixel-snapped.
 * @param {number} groupStart
 * @param {number} headerHeight
 * @returns {number}
 */
export function getRowVirtualizerScrollMargin(groupStart, headerHeight) {
  return roundCssPx(groupStart) + roundCssPx(headerHeight);
}

/**
 * Integer height of a measured row (`offsetHeight` is already whole pixels).
 * @param {Element | null | undefined} element
 * @returns {number}
 */
export function measureTableRowHeightPx(element) {
  if (!(element instanceof HTMLElement)) return TABLE_ROW_ESTIMATED_SIZE_PX;
  return Math.max(1, roundCssPx(element.offsetHeight));
}

/**
 * @param {number} rowCount
 * @returns {boolean}
 */
export function shouldVirtualizeTableGroupRows(rowCount) {
  return rowCount > TABLE_ROW_VIRTUALIZATION_THRESHOLD;
}

/**
 * Spacer heights for a table-body virtualizer that uses `scrollMargin`.
 * `item.start` includes scrollMargin; `getTotalSize()` does not.
 * @param {Array<{ start: number, end: number }>} virtualItems
 * @param {number} totalSize
 * @param {number} scrollMargin
 * @returns {{ paddingTop: number, paddingBottom: number }}
 */
export function getRowVirtualizerSpacerHeights(virtualItems, totalSize, scrollMargin) {
  const margin = roundCssPx(scrollMargin);
  const size = roundCssPx(totalSize);
  if (!virtualItems.length) {
    return { paddingTop: 0, paddingBottom: size };
  }
  const first = virtualItems[0];
  const last = virtualItems[virtualItems.length - 1];
  const paddingTop = roundCssPx(Math.max(0, first.start - margin));
  const paddingBottom = roundCssPx(Math.max(0, size - (last.end - margin)));
  return { paddingTop, paddingBottom };
}
